// client/src/utils/accessControl.js
//
// Чистые функции для проверки прав.
// НЕ используют React-хуки — вызываются из компонентов после useSelector.
//

export const SYSTEM_ROLES = {
  USER: 'user',
  CURATOR: 'curator',
  ADMIN: 'admin',
  SUPERADMIN: 'superadmin',
};

const EMPTY_RESOURCES = {
  routes: [],
  ontologies: [],
  admin_panels: [],
  game_actions: [],
};

// ============================================================================
// Извлечение данных из auth-среза
// ============================================================================

export function getAccess(auth) {
  return auth?.access || null;
}

export function getSystemRole(auth) {
  return auth?.access?.system_role || SYSTEM_ROLES.USER;
}

export function getResources(auth) {
  return auth?.access?.resources || EMPTY_RESOURCES;
}

export function getCharacters(auth) {
  return Array.isArray(auth?.access?.characters) ? auth.access.characters : [];
}

export function getWallet(auth) {
  return auth?.access?.wallet || { balance: 0, currency: 'uef_credits' };
}

export function getGameState(auth) {
  return auth?.access?.game_state || { emancipated: false, observing_character_id: null };
}

// ============================================================================
// Проверки ролей
// ============================================================================

export function isUser(auth) {
  return getSystemRole(auth) === SYSTEM_ROLES.USER;
}

export function isCurator(auth) {
  return getSystemRole(auth) === SYSTEM_ROLES.CURATOR;
}

export function isAdmin(auth) {
  const role = getSystemRole(auth);
  return role === SYSTEM_ROLES.ADMIN || role === SYSTEM_ROLES.SUPERADMIN;
}

export function isSuperAdmin(auth) {
  return getSystemRole(auth) === SYSTEM_ROLES.SUPERADMIN;
}

export function hasRole(auth, roles) {
  if (!Array.isArray(roles) || roles.length === 0) return true;
  return roles.includes(getSystemRole(auth));
}

// ============================================================================
// Проверки ресурсов
// ============================================================================

/**
 * Универсальная проверка ресурса.
 *
 * @param {object} auth — state.auth
 * @param {'route'|'ontology'|'admin_panel'|'game_action'} resourceType
 * @param {string} resourcePath
 * @returns {boolean}
 */
export function hasResource(auth, resourceType, resourcePath) {
  if (!resourcePath) return false;

  const key = {
    route: 'routes',
    ontology: 'ontologies',
    admin_panel: 'admin_panels',
    game_action: 'game_actions',
  }[resourceType];

  if (!key) return false;

  const list = getResources(auth)[key];
  if (!Array.isArray(list)) return false;

  return list.includes('*') || list.includes(resourcePath);
}

/**
 * Алиас для `hasResource(auth, 'route', path)`.
 */
export function hasRoute(auth, path) {
  return hasResource(auth, 'route', path);
}

/**
 * Алиас для `hasResource(auth, 'admin_panel', name)`.
 */
export function hasAdminPanel(auth, name) {
  return hasResource(auth, 'admin_panel', name);
}

/**
 * Алиас для `hasResource(auth, 'game_action', action)`.
 */
export function hasGameAction(auth, action) {
  return hasResource(auth, 'game_action', action);
}

/**
 * Алиас для `hasResource(auth, 'ontology', type)`.
 */
export function hasOntology(auth, type) {
  return hasResource(auth, 'ontology', type);
}

// ============================================================================
// Семантические обёртки (совместимость с существующим кодом)
// ============================================================================

export function canAccessRoute(auth, path) {
  return hasRoute(auth, path);
}

export function canAccessOntology(auth, type) {
  return hasOntology(auth, type);
}

export function canAccessPanel(auth, panel) {
  return hasAdminPanel(auth, panel);
}

export function canPerformGameAction(auth, action) {
  return hasGameAction(auth, action);
}

// ============================================================================
// Проверки персонажей
// ============================================================================

const IMMUTABLE_STATUSES = ['emancipated', 'dead', 'archived'];

export function getCharacter(auth, characterId) {
  if (!characterId) return null;
  return getCharacters(auth).find((c) => c.character_id === characterId) || null;
}

export function getCharacterRelationship(auth, characterId) {
  if (!characterId) return 'stranger';
  if (isAdmin(auth)) return 'admin';
  const char = getCharacter(auth, characterId);
  if (!char) return 'stranger';
  if (char.source === 'created') return 'owner';
  if (char.source === 'managed') return 'manager';
  if (char.source === 'bought') return 'owner';
  return 'stranger';
}

export function canManageCharacter(auth, characterId) {
  if (isAdmin(auth)) return true;
  const char = getCharacter(auth, characterId);
  if (!char) return false;
  if (IMMUTABLE_STATUSES.includes(char.status)) return false;
  return ['created', 'managed', 'bought'].includes(char.source);
}

export function canEmancipateCharacter(auth, characterId) {
  if (isAdmin(auth)) {
    const char = getCharacter(auth, characterId);
    return Boolean(char) && !IMMUTABLE_STATUSES.includes(char.status);
  }
  const char = getCharacter(auth, characterId);
  if (!char) return false;
  if (!['created', 'bought'].includes(char.source)) return false;
  if (IMMUTABLE_STATUSES.includes(char.status)) return false;
  return true;
}

// ============================================================================
// Игровая роль (observer/operator/curator)
// ============================================================================

export function getGameRole(auth, gameSession) {
  const gameState = getGameState(auth);
  const isConnected = Boolean(gameSession?.isConnected);
  if (!isConnected) return 'observer';
  return gameState.emancipated ? 'curator' : 'operator';
}

export function canOperate(auth, gameSession) {
  return getGameRole(auth, gameSession) === 'operator';
}

// ============================================================================
// Готовность access
// ============================================================================

export function isAccessReady(auth) {
  return auth?.accessChecked === true && auth?.access != null;
}